import { useState } from 'react';
import { Award, ExternalLink, MapPin, Calendar, Star, Filter } from 'lucide-react';
import { motion } from 'framer-motion';

// Programs, camps, and competitions for middle schoolers
const programs = [
  {
    id: 'scratch',
    name: 'Scratch Programming',
    category: 'Technology',
    type: 'Online Learning',
    emoji: '💻',
    color: 'from-orange-500 to-amber-500',
    description: 'Learn to code by making games and animations! Created by MIT.',
    ageRange: '8-16',
    cost: 'Free',
    link: 'https://scratch.mit.edu',
    highlights: ['Create games', 'Make animations', 'Share with friends']
  },
  {
    id: 'khan',
    name: 'Khan Academy',
    category: 'Learning',
    type: 'Online Learning',
    emoji: '📚',
    color: 'from-green-500 to-emerald-500',
    description: 'Free courses on everything - math, science, history, and more!',
    ageRange: 'All ages',
    cost: 'Free',
    link: 'https://www.khanacademy.org',
    highlights: ['Personalized learning', 'Practice exercises', 'Earn badges']
  },
  {
    id: 'mathcounts',
    name: 'MATHCOUNTS',
    category: 'Competitions',
    type: 'Competition',
    emoji: '🔢',
    color: 'from-blue-500 to-indigo-500',
    description: 'Middle school math competition with fun challenges!',
    ageRange: '6th-8th grade',
    cost: 'Free to join school team',
    link: 'https://www.mathcounts.org',
    highlights: ['Team competitions', 'Win prizes', 'Meet other math lovers']
  },
  {
    id: 'spelling-bee',
    name: 'Scripps National Spelling Bee',
    category: 'Competitions',
    type: 'Competition',
    emoji: '🐝',
    color: 'from-yellow-500 to-orange-500',
    description: 'The ultimate spelling challenge! Compete locally to nationally.',
    ageRange: 'Through 8th grade',
    cost: 'Free',
    link: 'https://spellingbee.com',
    highlights: ['School competitions', 'National TV finals', 'Cash prizes']
  },
  {
    id: 'first-lego',
    name: 'FIRST LEGO League',
    category: 'Robotics',
    type: 'Competition',
    emoji: '🤖',
    color: 'from-red-500 to-pink-500',
    description: 'Build LEGO robots and compete with teams worldwide!',
    ageRange: '9-14 years',
    cost: 'Team registration fee',
    link: 'https://www.firstinspires.org/robotics/fll',
    highlights: ['Build robots', 'Solve real problems', 'Teamwork']
  },
  {
    id: 'science-olympiad',
    name: 'Science Olympiad',
    category: 'Science',
    type: 'Competition',
    emoji: '🔬',
    color: 'from-purple-500 to-indigo-500',
    description: 'Team competition with 23 different science events!',
    ageRange: 'Middle & High School',
    cost: 'Free through school',
    link: 'https://www.soinc.org',
    highlights: ['23 events', 'Team medals', 'National competition']
  },
  {
    id: 'code-org',
    name: 'Code.org',
    category: 'Technology',
    type: 'Online Learning',
    emoji: '👨‍💻',
    color: 'from-cyan-500 to-blue-500',
    description: 'Learn computer science through fun activities and games!',
    ageRange: 'All ages',
    cost: 'Free',
    link: 'https://code.org',
    highlights: ['Hour of Code', 'App Lab', 'Game Lab']
  },
  {
    id: 'duke-tip',
    name: 'Duke TIP',
    category: 'Academic',
    type: 'Summer Program',
    emoji: '🎓',
    color: 'from-blue-600 to-indigo-600',
    description: 'Summer programs and online courses for gifted students',
    ageRange: '7th-12th grade',
    cost: 'Varies, scholarships available',
    link: 'https://tip.duke.edu',
    highlights: ['Summer camps', 'Online courses', 'Meet smart kids']
  }
];

const categories = ['All', 'Technology', 'Competitions', 'Learning', 'Robotics', 'Science', 'Academic'];
const types = ['All Types', 'Online Learning', 'Competition', 'Summer Program'];

export default function MSProgramsPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All Types');

  const filteredPrograms = programs.filter(p => {
    const catMatch = selectedCategory === 'All' || p.category === selectedCategory;
    const typeMatch = selectedType === 'All Types' || p.type === selectedType;
    return catMatch && typeMatch;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Cool Programs</h1>
            <p className="text-slate-300">Camps, competitions, and activities just for you!</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  selectedCategory === cat
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Programs Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {filteredPrograms.map((program, index) => (
          <motion.div
            key={program.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="glass-card p-6 hover:scale-[1.01] transition-transform"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${program.color} flex items-center justify-center text-2xl`}>
                {program.emoji}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{program.name}</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300">
                    {program.category}
                  </span>
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300">
                    {program.type}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-slate-300 text-sm mb-4">{program.description}</p>

            <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Calendar className="w-4 h-4" />
                <span>Ages: {program.ageRange}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Star className="w-4 h-4" />
                <span>{program.cost}</span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-slate-300 text-xs mb-2">Highlights:</p>
              <div className="flex flex-wrap gap-1">
                {program.highlights.map((h, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300">
                    {h}
                  </span>
                ))}
              </div>
            </div>

            <a
              href={program.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-gradient-to-r ${program.color} text-white font-medium hover:opacity-90 transition-opacity`}
            >
              Learn More
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        ))}
      </div>

      {filteredPrograms.length === 0 && (
        <div className="text-center py-12 text-slate-300">
          No programs found with these filters. Try changing your selection!
        </div>
      )}
    </div>
  );
}
